import {
  PAGE_PORTAL_COMPANY,
  PAGE_PORTAL_DASHBOARD,
  PAGE_PORTAL_EDUCATION_BILLING,
  PAGE_PORTAL_EDUCATION_EXPENSES,
  PAGE_PORTAL_EDUCATION_FEE_STRUCTURES,
  PAGE_PORTAL_EDUCATION_PROCUREMENT,
  PAGE_PORTAL_EDUCATION_REPORTS,
  PAGE_PORTAL_EDUCATION_STOCK_USAGE,
  PAGE_PORTAL_EDUCATION_STUDENTS,
  PAGE_PORTAL_FINANCE_INVOICES,
  PAGE_PORTAL_FINANCE_PAYMENTS,
  PAGE_PORTAL_FINANCE_RECEIPTS_LIST,
  PAGE_PORTAL_PROFILE,
  PORTAL_PREFIX
} from "@kivunova/kivufrontendcommon";
import {
  canAccessEducationAcademicNav,
  canAccessEducationAuditNav,
  canAccessEducationDashboard,
  canAccessEducationFinanceNav,
  canAccessEducationFinanceOperationsNav,
  canAccessEducationInventoryNav,
  canAccessEducationProcurementNav,
  canAccessEducationSettingsNav
} from "../../components/Navigation/constants/educationNavRoleAccess";
import type { SidebarNavConfigEntry } from "../types/sidebarNavConfigTypes";

const EDUCATION_PREFIX = `${PORTAL_PREFIX}/education`;

/**
 * Education portal nav — domain modules at top level (HLD §11).
 * Academics groups students and academic structure setup (programs → grades → streams).
 */
export const EDUCATION_SIDEBAR_CONFIG: SidebarNavConfigEntry[] = [
  {
    labelKey: "sidebar-overview",
    defaultLabel: "Overview",
    icon: "grid",
    path: `${PORTAL_PREFIX}/${PAGE_PORTAL_DASHBOARD}`,
    testId: "sidebar-nav-dashboard",
    visible: canAccessEducationDashboard
  },
  {
    labelKey: "sidebar-academics",
    defaultLabel: "Academics",
    icon: "school",
    path: `${EDUCATION_PREFIX}/${PAGE_PORTAL_EDUCATION_STUDENTS}`,
    testId: "sidebar-nav-academics",
    visible: canAccessEducationAcademicNav,
    children: [
      {
        labelKey: "sidebar-students",
        defaultLabel: "Students",
        icon: "person",
        path: `${EDUCATION_PREFIX}/${PAGE_PORTAL_EDUCATION_STUDENTS}`,
        testId: "sidebar-nav-education-students"
      },
      {
        labelKey: "sidebar-programs",
        defaultLabel: "Programs",
        icon: "category",
        path: `${EDUCATION_PREFIX}/programs`,
        testId: "sidebar-nav-education-programs"
      },
      {
        labelKey: "sidebar-grades",
        defaultLabel: "Grades",
        icon: "category",
        path: `${EDUCATION_PREFIX}/grades`,
        testId: "sidebar-nav-education-grades"
      },
      {
        labelKey: "sidebar-streams",
        defaultLabel: "Streams",
        icon: "category",
        path: `${EDUCATION_PREFIX}/streams`,
        testId: "sidebar-nav-education-streams"
      },
      {
        labelKey: "sidebar-billing-periods",
        defaultLabel: "Billing periods",
        icon: "receipt",
        path: `${EDUCATION_PREFIX}/billing-periods`,
        testId: "sidebar-nav-education-billing-periods"
      },
      {
        labelKey: "sidebar-departments",
        defaultLabel: "Departments",
        icon: "storeOutlined",
        path: `${EDUCATION_PREFIX}/departments`,
        testId: "sidebar-nav-education-departments"
      },
      {
        labelKey: "sidebar-student-categories",
        defaultLabel: "Student categories",
        icon: "person",
        path: `${EDUCATION_PREFIX}/student-categories`,
        testId: "sidebar-nav-education-student-categories"
      }
    ]
  },
  {
    labelKey: "sidebar-procurement",
    defaultLabel: "Procurement",
    icon: "shoppingCart",
    path: `${EDUCATION_PREFIX}/${PAGE_PORTAL_EDUCATION_PROCUREMENT}`,
    testId: "sidebar-nav-procurement",
    visible: canAccessEducationProcurementNav
  },
  {
    labelKey: "sidebar-finance",
    defaultLabel: "Finance",
    icon: "priceCheck",
    path: `${EDUCATION_PREFIX}/${PAGE_PORTAL_EDUCATION_FEE_STRUCTURES}`,
    testId: "sidebar-nav-finance",
    visible: canAccessEducationFinanceNav,
    children: [
      {
        labelKey: "sidebar-fee-structures",
        defaultLabel: "Fee structures",
        icon: "receipt",
        path: `${EDUCATION_PREFIX}/${PAGE_PORTAL_EDUCATION_FEE_STRUCTURES}`,
        testId: "sidebar-nav-education-fees"
      },
      {
        labelKey: "sidebar-billing-preview",
        defaultLabel: "Billing preview",
        icon: "priceCheck",
        path: `${EDUCATION_PREFIX}/${PAGE_PORTAL_EDUCATION_BILLING}`,
        testId: "sidebar-nav-education-billing"
      },
      {
        labelKey: "sidebar-expenses",
        defaultLabel: "Expenses",
        icon: "receipt",
        path: `${EDUCATION_PREFIX}/${PAGE_PORTAL_EDUCATION_EXPENSES}`,
        testId: "sidebar-nav-education-expenses"
      },
      {
        labelKey: "sidebar-payments",
        defaultLabel: "Payments",
        icon: "paymentOutlined",
        path: `${PORTAL_PREFIX}/${PAGE_PORTAL_FINANCE_PAYMENTS}`,
        testId: "sidebar-nav-finance-payments",
        visible: canAccessEducationFinanceOperationsNav
      },
      {
        labelKey: "sidebar-invoices",
        defaultLabel: "Invoices",
        icon: "receipt",
        path: `${PORTAL_PREFIX}/${PAGE_PORTAL_FINANCE_INVOICES}`,
        testId: "sidebar-nav-finance-invoices",
        visible: canAccessEducationFinanceOperationsNav
      },
      {
        labelKey: "sidebar-receipts",
        defaultLabel: "Receipts",
        icon: "receipt",
        path: `${PORTAL_PREFIX}/${PAGE_PORTAL_FINANCE_RECEIPTS_LIST}`,
        testId: "sidebar-nav-finance-receipts",
        visible: canAccessEducationFinanceOperationsNav
      }
    ]
  },
  {
    labelKey: "sidebar-inventory",
    defaultLabel: "Inventory",
    icon: "storeOutlined",
    path: `${EDUCATION_PREFIX}/${PAGE_PORTAL_EDUCATION_STOCK_USAGE}`,
    testId: "sidebar-nav-inventory",
    visible: canAccessEducationInventoryNav
  },
  {
    labelKey: "sidebar-audit",
    defaultLabel: "Audit",
    icon: "grid",
    path: `${EDUCATION_PREFIX}/${PAGE_PORTAL_EDUCATION_REPORTS}`,
    testId: "sidebar-nav-audit",
    visible: canAccessEducationAuditNav,
    children: [
      {
        labelKey: "sidebar-reports",
        defaultLabel: "Reports",
        icon: "grid",
        path: `${EDUCATION_PREFIX}/${PAGE_PORTAL_EDUCATION_REPORTS}`,
        testId: "sidebar-nav-education-reports"
      }
    ]
  },
  {
    labelKey: "sidebar-profile",
    defaultLabel: "Profile",
    icon: "person",
    path: `${PORTAL_PREFIX}/${PAGE_PORTAL_PROFILE}`,
    testId: "sidebar-nav-profile"
  },
  {
    labelKey: "sidebar-school-profile",
    defaultLabel: "School profile",
    icon: "school",
    path: `${PORTAL_PREFIX}/${PAGE_PORTAL_COMPANY}`,
    testId: "sidebar-nav-school-profile",
    visible: canAccessEducationSettingsNav
  },
  {
    labelKey: "sidebar-education-settings",
    defaultLabel: "Education settings",
    icon: "settings",
    path: `${EDUCATION_PREFIX}/settings`,
    testId: "sidebar-nav-education-settings",
    visible: canAccessEducationSettingsNav
  }
];
